import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import SortTabs from "@/components/base/SortTabs";
import EmptyState from "@/components/base/EmptyState";
import SoftwareCard from "@/components/base/SoftwareCard";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  allCategories,
  allSoftware,
  filterSoftware,
  sortSoftware,
} from "@/utils/software";
import type { Software, SortKey } from "@/utils/software";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [categoryId, setCategoryId] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentMeta({
    title: query
      ? `搜索「${query}」- 黄金网站软件app免费`
      : "搜索黄金投资软件 - 黄金网站软件app免费",
    description: query
      ? `黄金网站软件app免费为你搜索「${query}」相关的黄金投资软件与应用，提供免费下载与详细介绍。`
      : "在黄金网站软件app免费搜索黄金投资软件、行情 APP 与理财工具。",
    keywords: "黄金软件搜索,黄金投资软件,黄金行情app下载",
  });

  const results = filterSoftware({
    keyword,
    categoryId: categoryId === "全部" ? undefined : categoryId,
    sort,
  });
  const recommended = sortSoftware(allSoftware, "downloads").slice(0, 3);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParams(keyword.trim() ? { q: keyword.trim() } : {});
  };
  const handleDownload = (item: Software) => {
    setToast(`已打开「${item.name}」的示范下载链接（演示功能，无真实下载）`);
  };
  const resetFilters = () => {
    setCategoryId("全部");
    setSort("latest");
  };

  return (
    <SiteLayout>
      <div className="container-page py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

        <div className="mt-6 flex flex-col gap-2">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {query ? `「${query}」的搜索结果` : "搜索软件"}
          </h1>
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-primary-700">{results.length}</strong> 款相关软件
          </p>
        </div>

        <form
          onSubmit={submit}
          role="search"
          className="mt-4 flex w-full items-center gap-2 rounded-lg border border-background-300 bg-background-50 p-1.5"
        >
          <i className="ri-search-line ml-2 text-lg text-foreground-400" aria-hidden="true" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="search"
            placeholder="搜索黄金投资软件 / APP"
            aria-label="搜索软件"
            className="h-10 flex-1 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
          <button type="submit" className="btn-primary h-10 px-5">
            搜索
          </button>
        </form>

        <section className="card-surface mt-6 flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {["全部", ...allCategories.map((c) => c.name)].map((name) => {
              const cat = allCategories.find((c) => c.name === name);
              const value = cat ? cat.id : "全部";
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setCategoryId(value)}
                  className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    categoryId === value
                      ? "border-primary-500 bg-primary-500 text-foreground-950"
                      : "border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-700"
                  }`}
                >
                  {cat && <i className={cat.icon} />}
                  {name}
                </button>
              );
            })}
          </div>
          <SortTabs value={sort} onChange={setSort} />
        </section>

        {results.length > 0 ? (
          <section
            className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
            data-product-shop
          >
            {results.map((item) => (
              <SoftwareCard key={item.id} item={item} onDownload={handleDownload} />
            ))}
          </section>
        ) : (
          <>
            <div className="mt-6">
              <EmptyState
                icon="ri-search-eye-line"
                title="暂无相关软件"
                desc={`没有找到与「${query || keyword || "关键词"}」匹配的软件，换个关键词，或看看下面的热门推荐。`}
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
            </div>
            <section className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-fire-line text-accent-500" />
                热门推荐
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-product-shop>
                {recommended.map((item) => (
                  <SoftwareCard key={item.id} item={item} onDownload={handleDownload} />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
      <Toast message={toast} onClose={() => setToast(null)} />
    </SiteLayout>
  );
}